/**
 * Playback Timeline (Sprint 6C — UMXP).
 *
 * Single reference for playback-relative events / markers.
 * Future features (ads, AI, interactive) consume this model — do not invent parallel timelines.
 */

export var TimelineMarkerType = Object.freeze({
  CINEMATIC_INTRO: "cinematic_intro",
  MAIN_CONTENT: "main_content",
  CHAPTER: "chapter",
  RESUME_POSITION: "resume_position",
  SKIP_INTRO: "skip_intro",
  SKIP_CREDITS: "skip_credits",
  AD_BREAK: "ad_break",
  AI_MARKER: "ai_marker",
  INTERACTIVE: "interactive",
  CREDITS: "credits",
  LIVE_DVR_WINDOW: "live_dvr_window",
});

/**
 * @param {object} [options]
 * @param {object} [options.bus]
 */
export function createPlaybackTimeline(options) {
  options = options || {};
  var markers = [];
  var contentDuration = 0;
  var introDuration = 0;

  function list() {
    return markers.slice().sort(function (a, b) {
      return (a.start || 0) - (b.start || 0);
    });
  }

  /**
   * @param {object} marker
   * @param {string} marker.type TimelineMarkerType
   * @param {number} [marker.start]
   * @param {number} [marker.end]
   * @param {string} [marker.id]
   * @param {object} [marker.meta]
   */
  function addMarker(marker) {
    if (!marker || !marker.type) return null;
    var row = {
      id: marker.id || marker.type + "_" + Date.now().toString(36),
      type: marker.type,
      start: marker.start != null ? Number(marker.start) : 0,
      end: marker.end != null ? Number(marker.end) : null,
      meta: marker.meta || null,
    };
    // Replace same id
    markers = markers.filter(function (m) {
      return m.id !== row.id;
    });
    markers.push(row);
    if (options.bus && typeof options.bus.publish === "function") {
      options.bus.publish("TIMELINE_MARKER_ADDED", row);
    }
    return row;
  }

  function removeMarker(id) {
    markers = markers.filter(function (m) {
      return m.id !== id;
    });
  }

  function clear() {
    markers = [];
  }

  function setContentDuration(sec) {
    contentDuration = Math.max(0, Number(sec) || 0);
  }

  function setIntroDuration(sec) {
    introDuration = Math.max(0, Number(sec) || 0);
  }

  function getContentDuration() {
    return contentDuration;
  }

  function getIntroDuration() {
    return introDuration;
  }

  /**
   * Markers active at media-relative time (main content clock).
   * @param {number} timeSeconds
   */
  function getActiveMarkers(timeSeconds) {
    var t = Number(timeSeconds) || 0;
    return list().filter(function (m) {
      var start = m.start || 0;
      var end = m.end != null ? m.end : start;
      if (m.type === TimelineMarkerType.RESUME_POSITION) {
        return Math.abs(start - t) < 0.5;
      }
      if (m.end == null) {
        return t >= start;
      }
      return t >= start && t <= end;
    });
  }

  function getMarkersByType(type) {
    return list().filter(function (m) {
      return m.type === type;
    });
  }

  /**
   * Seed standard markers from a PlaybackSession snapshot / Experience layers.
   * @param {object} sessionOrSnap
   */
  function seedFromSession(sessionOrSnap) {
    var snap =
      sessionOrSnap && typeof sessionOrSnap.snapshot === "function"
        ? sessionOrSnap.snapshot()
        : sessionOrSnap || {};
    var intro = snap.intro || {};
    var resume = snap.resume || {};
    var experience = snap.experience || {};
    var live = snap.live || {};

    if (intro.url) {
      addMarker({
        id: "intro",
        type: TimelineMarkerType.CINEMATIC_INTRO,
        start: 0,
        end: null,
        meta: { url: intro.url, layerId: intro.layerId },
      });
      addMarker({
        id: "skip_intro",
        type: TimelineMarkerType.SKIP_INTRO,
        start: 0,
        meta: { enabled: true },
      });
    }

    addMarker({
      id: "main",
      type: TimelineMarkerType.MAIN_CONTENT,
      start: 0,
      end: null,
      meta: { contentId: (snap.content && snap.content.contentId) || null },
    });

    if (resume.position > 0) {
      addMarker({
        id: "resume",
        type: TimelineMarkerType.RESUME_POSITION,
        start: resume.position,
        meta: { source: resume.source },
      });
    }

    var layers = experience.layers || [];
    layers.forEach(function (layer, i) {
      if (!layer) return;
      if (layer.kind === "credits" || layer.type === "credits") {
        addMarker({
          id: "credits_" + i,
          type: TimelineMarkerType.CREDITS,
          start: layer.start != null ? layer.start : null,
          meta: layer,
        });
        addMarker({
          id: "skip_credits_" + i,
          type: TimelineMarkerType.SKIP_CREDITS,
          start: layer.start != null ? layer.start : null,
          meta: layer,
        });
      }
    });

    if (live.isLive && live.dvrWindowSeconds) {
      addMarker({
        id: "dvr",
        type: TimelineMarkerType.LIVE_DVR_WINDOW,
        start: -Number(live.dvrWindowSeconds),
        end: 0,
        meta: { windowSeconds: live.dvrWindowSeconds },
      });
    }

    return list();
  }

  function snapshot() {
    return {
      markers: list(),
      contentDuration: contentDuration,
      introDuration: introDuration,
    };
  }

  return {
    addMarker: addMarker,
    removeMarker: removeMarker,
    clear: clear,
    list: list,
    getActiveMarkers: getActiveMarkers,
    getMarkersByType: getMarkersByType,
    seedFromSession: seedFromSession,
    setContentDuration: setContentDuration,
    setIntroDuration: setIntroDuration,
    getContentDuration: getContentDuration,
    getIntroDuration: getIntroDuration,
    snapshot: snapshot,
    markerTypes: TimelineMarkerType,
  };
}

export default createPlaybackTimeline;
